import { useState, useCallback } from 'react';
import { ServiceFilters, ServiceSortOption } from '../types/service.types';

/**
 * Hook for managing service filter and sort state
 */
export const useServiceFilters = (
  initialFilters: Partial<ServiceFilters> = {},
  initialSort: ServiceSortOption = 'popular'
) => {
  const [filters, setFilters] = useState<ServiceFilters>({
    category: initialFilters.category || 'all',
    search: initialFilters.search || '',
    minPrice: initialFilters.minPrice,
    maxPrice: initialFilters.maxPrice,
    minDuration: initialFilters.minDuration,
    maxDuration: initialFilters.maxDuration,
  });

  const [sortBy, setSortBy] = useState<ServiceSortOption>(initialSort);

  const updateFilter = useCallback(
    <K extends keyof ServiceFilters>(key: K, value: ServiceFilters[K]) => {
      setFilters((prev) => ({
        ...prev,
        [key]: value,
      }));
    },
    []
  );

  const updateSort = useCallback((value: ServiceSortOption) => {
    setSortBy(value);
  }, []);

  const clearFilters = useCallback(() => {
    setFilters({
      category: 'all',
      search: '',
      minPrice: undefined,
      maxPrice: undefined,
      minDuration: undefined,
      maxDuration: undefined,
    });
    setSortBy('popular');
  }, []);

  const hasActiveFilters =
    filters.category !== 'all' ||
    !!filters.search ||
    filters.minPrice !== undefined ||
    filters.maxPrice !== undefined ||
    filters.minDuration !== undefined ||
    filters.maxDuration !== undefined;

  return {
    filters,
    sortBy,
    updateFilter,
    updateSort,
    clearFilters,
    hasActiveFilters,
  };
};
